import { ElMessageBox } from "element-plus";
import {
  getRequest,
  postRequest,
  putRequest,
  setAxiosDefaultBearerToken,
} from "~/apis/requests/useApiCall";
import { Permission, User } from "~/types";
import Feature from "@/types/Feature";
import { PusherConfig } from "~/types/pusherConfig";
import { AgentSetting } from "~/types/settings/AgentSetting";
import { Hierarchy } from "~/types/enums/positionalHierarchy";
import { useAgentsStore } from "~/stores/agentsStore";
import { TicketGroupInfo } from "~/projects/help-desk/types/ticketGroup";
import { ClientStatus } from "~/types/enums/ClientStatus";

type AuthData = {
  user: User;
  token: string;
  pusher_config: PusherConfig;
};

type ActivePingResponse = {
  next_after: string;
  type: string;
  // Absent when the account is healthy: the backend only describes the client
  // when it is disabled or has a notice to show. Declaring it required is what
  // hid the crash below from the compiler.
  client?: {
    status: ClientStatus;
    message: string;
  };
};

type ClientInfoResponse = {
  client_status: ClientStatus;
  client_message: string;
};

export const useAuthStore = defineStore("auth", {
  state: () => ({
    _user: {} as User,
    _pusherConfig: {} as PusherConfig,
    _isAuthenticated: false,
    _token: null as string | null,
    _permissions: [] as Array<Permission>,
    _features: {} as Feature,
    _agentSetting: {} as AgentSetting,
    _randomId: "",
    _clientStatus: ClientStatus.ACTIVE,
    _clientMessage: "" as string,
  }),
  persist: true,

  getters: {
    user: (state) => state._user,
    token: (state) => state._token,
    permissions: (state) => state._permissions,
    isAuthenticated: (state) => state._isAuthenticated,
    features: (state) => state._features,
    randomId: (state) => state._randomId,
    agentSetting: (state) => state._agentSetting,
    isMember: (state) => state._user.hierarchical_position === Hierarchy.MEMBER,
    clientStatus: (state) => state._clientStatus,
    clientMessage: (state) => state._clientMessage,
    isClientActive: (state) => state._clientStatus === ClientStatus.ACTIVE,
  },

  actions: {
    async login(loginRequestData: { email: string; password: string }) {
      const { response, error } = await postRequest<AuthData>(
        `login`,
        loginRequestData,
      );

      if (error) {
        await ElMessageBox({
          type: "error",
          title: "Login Failed",
          message: error.response?.data?.message || error.message,
        });
        return;
      }

      const authData = response.data;

      this.setUser(authData.user);
      this.setBearerToken(authData.token);
      this.setIsAuthenticated(true);
      this.setRandomId();
      this.setPusherConfig(authData.pusher_config);
      return authData;
    },

    async requestOtherBrowserSessionsLogout(): Promise<boolean> {
      const { error } = await postRequest<any>(
        "cc/agent/requestBrowserSessionsLogout",
        {
          requesterRandomId: this._randomId,
        },
      );
      return error === null;
    },

    async getCsrfToken() {
      return await getRequest("sanctum/csrf-cookie", false);
    },

    async getPermissions() {
      const { response, error } = await getRequest<Permission[]>(
        "user-permissions",
      );
      if (!error) {
        this._permissions = response.data;
      }
    },
    async getActivatedFeatures() {
      const { response, error } = await getRequest<Feature>("features");
      if (!error) {
        this._features = response.data;
      }
    },

    async logout() {
      const { response, error } = await postRequest<any>("logout");

      if (!error) {
        return response.data;
      }
    },

    async resetPassword(data: object) {
      const { response, error } = await putRequest<any>("reset-password", data);

      if (!error) {
        const res = response.data;

        this.setBearerToken(res.token);

        return res;
      }

      if (error) {
        const errors = error.response?.data?.errors || {};

        let formattedErrors = `<ul style="list-style-type: disc; padding-left: 20px;">`;

        Object.keys(errors).forEach((key) => {
          errors[key].forEach((message) => {
            formattedErrors += `<li>${message}</li>`;
          });
        });

        formattedErrors += "</ul>";

        ElMessageBox({
          title: "Password Reset Failed",
          message: Object.keys(errors).length ? formattedErrors : error.message,
          dangerouslyUseHTMLString: true,
          type: "info",
        });

        return null;
      }
    },

    async updateUserInfo(userInfo: any) {
      const agentsStore = useAgentsStore();
      const { response, error } = await postRequest<any>(
        `agent/change-info`,
        userInfo,
        true,

        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      if (!error) {
        await agentsStore.getAgent(response.data.id);
        this.setUser(agentsStore.selectedAgent);
        return this.user;
      }
      ElMessageBox({
        title: "Failed",
        message: error.response?.data.message,
        type: "error",
      });
      return error.response;
    },
    async getClientInfo() {
      const { response, error } = await getRequest<ClientInfoResponse>(
        "client-info",
      );
      if (!error) {
        this._clientStatus = response.data.client_status;
        this._clientMessage = response.data.client_message;
        return response.data;
      }
      return null;
    },
    async postActivePing() {
      const { response, error } = await postRequest<ActivePingResponse>(
        "agent/active-ping",
        [],
      );
      if (!error && response) {
        const { client } = response;

        /*
         * Only when there is something to say about the account.
         *
         * The backend sends `client` when it is disabled or carrying a notice,
         * and sends nothing at all when it is healthy — which is almost always.
         * Reading through it threw here once a minute on every deployment, and
         * because it threw, the inactive-client check that runs straight after
         * this never ran either. A quiet answer means nothing has changed.
         */
        if (client) {
          this._clientStatus = client?.status;
          this._clientMessage = client.message;
        }

        return response.data;
      }
      return null;
    },
    async changeStatus(status: string, break_reason?: string) {
      const { response, error } = await postRequest<User>(
        "agent/change-status",
        { status, break_reason },
      );
      if (!error) {
        this._user = response.data;
        return response.data;
      }
      return null;
    },

    // mutations
    setUser(user: User) {
      this._user = user;
    },
    setBearerToken(token: string) {
      this._token = token;
      setAxiosDefaultBearerToken(token);
    },
    setIsAuthenticated(value: boolean) {
      this._isAuthenticated = value;
    },
    setPusherConfig(config: PusherConfig) {
      this._pusherConfig = config;
    },
    setRandomId() {
      this._randomId = Math.random().toString(36).substring(7);
    },
    setClientStatus(status: ClientStatus) {
      this._clientStatus = status;
    },
    setClientMessage(message: string) {
      this._clientMessage = message;
    },
  },
});
